import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardTitle, CardContent, CardDescription } from '@voilajsx/uikit/card';
import { Button } from '@voilajsx/uikit/button';
import { Alert, AlertTitle, AlertDescription } from '@voilajsx/uikit/alert';
import { ValidatedInput } from '@voilajsx/uikit/form';
import { 
  Edit, 
  Image, 
  AlertCircle,
  CheckCircle,
  Video,
  Eye,
  ExternalLink
} from 'lucide-react';
import { config } from '../../../../auth/config';
import { SEO } from '../../../../../shared/components';

interface GalleryItemData {
  id: number;
  title: string;
  description?: string;
  fileKey: string;
  fileName: string;
  fileType: 'image' | 'video';
  mimeType: string;
  fileSize: number;
  url: string;
  isActive: boolean;
  order: number;
  createdAt: string;
}

const EditGalleryItemPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    isActive: true,
    order: 0
  });
  const [galleryItem, setGalleryItem] = useState<GalleryItemData | null>(null);
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [imageLoadError, setImageLoadError] = useState(false);

  // Fetch gallery item data on component mount
  useEffect(() => {
    const fetchGalleryItem = async () => {
      if (!id) {
        setError('Gallery item ID is required');
        setFetchLoading(false);
        return;
      }

      try {
        const token = localStorage.getItem('auth_token');
        if (!token) throw new Error('User is not authenticated');

        const response = await fetch(`${config.api.baseUrl}/api/gallery/${id}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error('Gallery item not found');
        }

        const data = await response.json();
        const itemData = data.item;
        
        setGalleryItem(itemData);
        setFormData({
          title: itemData.title || '',
          description: itemData.description || '',
          isActive: itemData.isActive ?? true,
          order: itemData.order || 0
        });
      } catch (err: any) {
        setError(err.message || 'Failed to fetch gallery item');
      } finally {
        setFetchLoading(false);
      }
    };

    fetchGalleryItem();
  }, [id]);

  const handleChange = (value: string | boolean | number, fieldId?: string) => {
    if (!fieldId) return;
    setFormData(prev => ({ ...prev, [fieldId]: value }));
  };

  const validate = () => {
    return formData.title.trim() !== '';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    if (!validate()) {
      setError('Please fill all required fields.');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('auth_token');
      if (!token) throw new Error('User is not authenticated');

      const response = await fetch(`${config.api.baseUrl}/api/gallery/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const json = await response.json();
        throw new Error(json.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      setSuccess(true);
      setTimeout(() => navigate('/gallery'), 2000);
    } catch (err: any) {
      setError(err.message || 'Failed to update gallery item');
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = () => {
    if (galleryItem?.url) {
      window.open(galleryItem.url, '_blank');
    }
  };

  if (fetchLoading) {
    return (
      <div className="container mx-auto px-4 max-w-4xl py-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading gallery item...</p>
        </div>
      </div>
    );
  }

  if (error && !galleryItem) {
    return (
      <div className="container mx-auto px-4 max-w-4xl py-8">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
        <Button 
          onClick={() => navigate('/gallery')} 
          className="mt-4"
          variant="outline"
        >
          Back to Gallery
        </Button>
      </div>
    );
  }

  const FileIcon = galleryItem?.fileType === 'image' ? Image : Video;

  return (
    <>
      <SEO
        title="Edit Gallery Item - Admin Panel"
        description="Edit gallery item details"
      />
      <div className="container mx-auto px-4 max-w-4xl py-8">
        <div className="space-y-6">
          {/* Header */}
          <div className="bg-gradient-to-r from-orange-600 via-amber-600 to-yellow-600 rounded-lg p-6 shadow-lg">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 rounded-lg backdrop-blur-sm">
                <Edit className="h-8 w-8 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold tracking-tight text-white">
                  Edit Gallery Item
                </h1>
                <p className="mt-1 text-orange-100 text-sm">
                  Update the details of "{galleryItem?.title}"
                </p>
              </div>
            </div>
          </div>

          {/* Preview Card */}
          {galleryItem && (
            <Card className="border-t-4 border-t-blue-600 shadow-md overflow-hidden p-0">
              <div className="bg-gradient-to-r from-blue-100 to-sky-100 px-6 py-5">
                <CardTitle className="flex items-center gap-2">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <FileIcon className="h-5 w-5 text-blue-600" />
                  </div>
                  Current File
                </CardTitle>
                <CardDescription className="mt-1.5">
                  Preview of the current gallery item
                </CardDescription>
              </div>
              
              <CardContent className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* File Info */}
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm font-medium text-gray-700">File Information</p>
                      <div className="mt-2 text-sm text-gray-600 space-y-1">
                        <p><span className="font-medium">Name:</span> {galleryItem.fileName}</p>
                        <p><span className="font-medium">Type:</span> {galleryItem.fileType.toUpperCase()} ({galleryItem.mimeType})</p>
                        <p><span className="font-medium">Size:</span> {(galleryItem.fileSize / 1024 / 1024).toFixed(2)} MB</p>
                        <p><span className="font-medium">Status:</span> 
                          <span className={`ml-1 px-2 py-1 rounded-full text-xs ${
                            galleryItem.isActive 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {galleryItem.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </p>
                        <p><span className="font-medium">Order:</span> {galleryItem.order}</p>
                        <p><span className="font-medium">Created:</span> {new Date(galleryItem.createdAt).toLocaleDateString()}</p>
                      </div>
                    </div>
                    
                    <Button
                      type="button"
                      onClick={handlePreview}
                      size="sm"
                      variant="outline"
                      className="border-blue-200 text-blue-600 hover:bg-blue-50"
                    >
                      <Eye className="mr-1 h-4 w-4" />
                      Preview File
                      <ExternalLink className="ml-1 h-3 w-3" />
                    </Button>
                  </div>

                  {/* Preview */}
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">Preview</p>
                    <div className="border rounded-lg p-4 bg-gray-50">
                      {galleryItem.fileType === 'image' ? (
                        imageLoadError ? (
                          <div className="text-center text-gray-500 py-8">
                            <p>Image preview not available</p>
                          </div>
                        ) : (
                          <img
                            src={galleryItem.url}
                            alt={galleryItem.title}
                            className="max-w-full h-auto rounded max-h-48 object-contain mx-auto"
                            onError={() => setImageLoadError(true)}
                          />
                        )
                      ) : (
                        <div className="text-center py-8">
                          <Video className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                          <p className="text-sm text-gray-500">Video preview</p>
                          <p className="text-xs text-gray-400">{galleryItem.fileName}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                <p className="text-sm text-gray-500 mt-4">
                  Note: To change the file, please delete this item and upload a new one.
                </p>
              </CardContent>
            </Card>
          )}

          {/* Edit Form Card */}
          <Card className="border-t-4 border-t-orange-600 shadow-md overflow-hidden p-0">
            <div className="bg-gradient-to-r from-orange-100 to-amber-100 px-6 py-5">
              <CardTitle className="flex items-center gap-2">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <Edit className="h-5 w-5 text-orange-600" />
                </div>
                Item Information
              </CardTitle>
              <CardDescription className="mt-1.5">
                Update the gallery item details and settings
              </CardDescription>
            </div>
            
            <CardContent className="p-6">
              {error && (
                <Alert variant="destructive" className="mb-6">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Error</AlertTitle>
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              
              {success && (
                <Alert className="mb-6 border-green-200 bg-green-50">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <AlertTitle className="text-green-800">Success</AlertTitle>
                  <AlertDescription className="text-green-700">
                    Gallery item updated successfully. Redirecting...
                  </AlertDescription>
                </Alert>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <ValidatedInput
                    id="title"
                    label="Title"
                    required
                    value={formData.title}
                    onChange={(value) => handleChange(value, 'title')}
                    placeholder="Enter gallery item title"
                  />

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Display Order
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={formData.order}
                      onChange={(e) => handleChange(parseInt(e.target.value) || 0, 'order')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      placeholder="Display order (0 = first)"
                    />
                    <p className="text-xs text-gray-500 mt-1">Lower numbers appear first</p>
                  </div>
                </div>

                <ValidatedInput
                  id="description"
                  label="Description (Optional)"
                  value={formData.description}
                  onChange={(value) => handleChange(value, 'description')}
                  placeholder="Brief description of the gallery item"
                />

                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(e) => handleChange(e.target.checked, 'isActive')}
                      className="mr-2 h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      Active (visible on homepage)
                    </span>
                  </label>
                  <p className="text-xs text-gray-500 mt-1">
                    Uncheck to hide this item from the public gallery
                  </p>
                </div>

                <div className="flex gap-4 pt-4">
                  <Button 
                    type="submit" 
                    disabled={loading}
                    className="bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700 disabled:opacity-50"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Updating...
                      </>
                    ) : (
                      <>
                        <Edit className="mr-2 h-4 w-4" />
                        Update Gallery Item
                      </>
                    )}
                  </Button>
                  
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => navigate('/gallery')}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
};

export default EditGalleryItemPage;
